import React from 'react';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaApple, FaLock, FaEye, FaUserShield, FaFileAlt } from 'react-icons/fa';
import './Privacy.css';

const Privacy = () => {
  const lastUpdated = "December 15, 2024";

  const privacyPrinciples = [
    {
      icon: <FaShieldAlt />,
      title: "Data Protection",
      description: "Your skincare data is encrypted and stored securely on your device and iCloud."
    },
    {
      icon: <FaApple />,
      title: "Apple Ecosystem",
      description: "We use Apple's secure infrastructure for all data syncing and storage."
    },
    {
      icon: <FaLock />,
      title: "No Third Parties",
      description: "We never share your personal information with advertisers or third parties."
    },
    {
      icon: <FaEye />,
      title: "Transparency",
      description: "You have full control over your data and can export or delete it anytime."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="privacy-page">
      <div className="container">
        {/* Hero Section */}
        <motion.section 
          className="privacy-hero"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="privacy-hero-content">
            <h1 className="privacy-title">
              Privacy Policy
            </h1>
            <p className="privacy-subtitle">
              Your privacy is fundamental to how we design DailySkin. This policy explains how we collect, use, and protect your information.
            </p>
            <div className="last-updated">
              <FaFileAlt />
              <span>Last updated: {lastUpdated}</span>
            </div>
          </div>
        </motion.section>

        {/* Privacy Principles */}
        <motion.section 
          className="privacy-principles"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="section-title">Our Privacy Principles</h2>
          <div className="principles-grid">
            {privacyPrinciples.map((principle, index) => (
              <motion.div
                key={index}
                className="principle-card"
                variants={itemVariants}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="principle-icon">
                  {principle.icon}
                </div>
                <h3>{principle.title}</h3>
                <p>{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Privacy Policy Content */}
        <motion.section 
          className="privacy-content"
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="content-section">
            <h2>Information We Collect</h2>
            <div className="content-block">
              <h3>Personal Information</h3>
              <p>
                DailySkin is designed to minimize data collection. We only collect information that is necessary to provide our service:
              </p>
              <ul>
                <li><strong>Skincare Data:</strong> Routine entries, product information, progress tracking, and photos you add to your skincare journey</li>
                <li><strong>App Usage:</strong> Basic analytics to improve app performance and user experience</li>
                <li><strong>Device Information:</strong> iOS version and device type for compatibility purposes</li>
              </ul>
              
              <h3>Information We Don't Collect</h3>
              <p>We explicitly do not collect:</p>
              <ul>
                <li>Personal identification information (name, email, phone number)</li>
                <li>Location data or personal identification beyond app usage</li>
                <li>Contact information or address book access</li>
                <li>Financial or payment information</li>
                <li>Browsing history or data from other apps</li>
              </ul>
            </div>
          </div>

          <div className="content-section">
            <h2>How We Use Your Information</h2>
            <div className="content-block">
              <p>The information we collect is used solely to:</p>
              <ul>
                <li>Display your skincare routines and progress tracking</li>
                <li>Sync your data across your Apple devices via iCloud</li>
                <li>Send you skincare reminder notifications you've configured</li>
                <li>Improve app performance and fix bugs</li>
                <li>Provide customer support when you contact us</li>
              </ul>
              
              <div className="highlight-box">
                <FaUserShield />
                <div>
                  <strong>Important:</strong> We never use your skincare information for advertising, marketing, or any purpose beyond providing the DailySkin service.
                </div>
              </div>
            </div>
          </div>

          <div className="content-section">
            <h2>Data Storage and Security</h2>
            <div className="content-block">
              <h3>Local Storage</h3>
              <p>
                All your skincare data is primarily stored locally on your device using Apple's secure Core Data framework. This means your information is always accessible to you, even when offline.
              </p>
              
              <h3>iCloud Sync</h3>
              <p>
                When you enable iCloud sync, your skincare data is securely transmitted and stored in your personal iCloud account using Apple's end-to-end encryption. We cannot access this data - only you can, from your Apple devices signed into the same iCloud account.
              </p>
              
              <h3>Security Measures</h3>
              <ul>
                <li>All data transmission uses industry-standard encryption (TLS 1.2+)</li>
                <li>Local data is protected by iOS's built-in security features</li>
                <li>No data is stored on our servers - everything stays within Apple's ecosystem</li>
                <li>Regular security updates through App Store releases</li>
              </ul>
            </div>
          </div>

          <div className="content-section">
            <h2>Data Sharing and Third Parties</h2>
            <div className="content-block">
              <p>
                <strong>We do not sell, rent, or share your personal information with third parties.</strong> Your skincare data remains private to you.
              </p>
              
              <h3>Service Providers</h3>
              <p>We work with the following service providers who may process limited data:</p>
              <ul>
                <li><strong>Apple Inc.:</strong> For app distribution, iCloud sync, and push notifications</li>
                <li><strong>Analytics Services:</strong> Anonymous app usage statistics to improve performance (no personal data)</li>
              </ul>
              
              <h3>Legal Requirements</h3>
              <p>
                We may disclose information if required by law, such as in response to a valid subpoena or court order. However, since we collect minimal data and most information is stored in your iCloud account, such requests would typically need to be directed to Apple.
              </p>
            </div>
          </div>

          <div className="content-section">
            <h2>Your Rights and Controls</h2>
            <div className="content-block">
              <p>You have complete control over your data in DailySkin:</p>
              
              <h3>Data Access</h3>
              <ul>
                <li>View all your skincare data directly in the app</li>
                <li>Export your routine history and progress data through the app's export feature</li>
              </ul>
              
              <h3>Data Modification</h3>
              <ul>
                <li>Edit, update, or delete any skincare information at any time</li>
                <li>Control which data syncs to iCloud</li>
                <li>Manage notification preferences for your routines</li>
              </ul>
              
              <h3>Data Deletion</h3>
              <ul>
                <li>Delete individual entries or all app data from the app settings</li>
                <li>Disable iCloud sync to stop data synchronization</li>
                <li>Uninstall the app to remove all local data</li>
              </ul>
            </div>
          </div>

          <div className="content-section">
            <h2>Children's Privacy</h2>
            <div className="content-block">
              <p>
                DailySkin is designed for users of all ages, including families building healthy skincare habits together. We do not knowingly collect personal information from children under 13 without parental consent. If you believe a child has provided personal information through the app, please contact us immediately.
              </p>
              
              <p>
                For families using DailySkin together through Family Sharing, parents maintain control over their children's access to the app and any associated data sharing.
              </p>
            </div>
          </div>

          <div className="content-section">
            <h2>International Users</h2>
            <div className="content-block">
              <p>
                DailySkin is available globally through the App Store. Your data is processed in accordance with Apple's global infrastructure and this privacy policy, regardless of your location.
              </p>
              
              <p>
                For users in the European Union, you have additional rights under GDPR, including the right to data portability, rectification, and erasure. These rights can be exercised directly through the app's data management features.
              </p>
            </div>
          </div>

          <div className="content-section">
            <h2>Changes to This Policy</h2>
            <div className="content-block">
              <p>
                We may update this privacy policy from time to time to reflect changes in our practices or applicable laws. When we make changes:
              </p>
              <ul>
                <li>We'll update the "Last Updated" date at the top of this policy</li>
                <li>Significant changes will be communicated through the app or App Store updates</li>
                <li>Continued use of DailySkin after changes indicates acceptance of the updated policy</li>
              </ul>
            </div>
          </div>

          <div className="content-section">
            <h2>Contact Us</h2>
            <div className="content-block">
              <p>
                If you have questions about this privacy policy or how we handle your data, please contact us:
              </p>
              
              <div className="contact-info">
                <div className="contact-method">
                  <strong>Email:</strong>
                  <a href="mailto:privacy@dailyskin-app.com">privacy@dailyskin-app.com</a>
                </div>
                <div className="contact-method">
                  <strong>Support:</strong>
                  <a href="mailto:support@dailyskin-app.com">support@dailyskin-app.com</a>
                </div>
              </div>
              
              <p>
                We typically respond to privacy-related inquiries within 48 hours during business days.
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Privacy;
